import React, { useMemo, useCallback, useState, useContext, useEffect } from 'react';
import { Rate } from 'antd';
import Modal from 'antd/lib/modal/Modal';
import { PaperIcon } from '@nackle/paper';
import { getString } from '@nackle/intl-tools';
import { Button } from 'antd';
import { useCourseDetailsQuery } from '../../../gql/hooks';
import { Translate } from 'app/services';
import { LearningObjectItem } from './components';
import { PageContainer, PageContent } from 'app/components/common';
import { Link, SimpleCollapse } from '../../common';
import { getDefaultCourseBanner } from '../../../const';
import { LoadingPopup } from '../../common/learningListController/loadingPopup/LoadingPopup';
import { ACTIVE_THEME_NAME } from '../../../branding';
import Certificate from '../../common/certificate';
import { InfoBanner } from '../../common/InfoBanner';
import ResourceList from '../../common/resourceList';
import { BreadcrumbContext, useBreadcrumbs } from '../../../breadcrumbs/BreadcrumbContext';
import { Navigation, Routes } from '../../../navigation';
import { CourseUtils } from '../../../utils';
import './styles.less';
import { RatingSection } from './RatingSection';
import useIlt from '../../../hooks/useILT';
import Sessions from '../../SessionView/Sessions';

export const CourseDetailsPage = ( props ) => {
    const courseId = useMemo( () => props?.route?.params?.courseId, [ props ] );
    const { data, loading, refetch, fetchAnew, error } = useCourseDetailsQuery( courseId );
    const { leaveCrumb } = useContext( BreadcrumbContext );
    const [ disableLearningObjects, setDisableLearningObjects ] = useState( false );

    function updateLessonStatus() {
        refetch();
    }

    const { loadIlt, isIltCourse, ilt, forceLoading, loading: iltLoading, iltRegistrationInfo, updateRegistration } = useIlt( updateLessonStatus );
    const breadcrumbs = useBreadcrumbs();

    const [ item, setItem ] = useState( data );

    useEffect( () => {
        setItem( data );

        if ( isIltCourse( data ) ) {
            loadIlt( data );
        }

    }, [ data ] );

    useEffect( () => {
        if ( props?.route?.params?.courseId ) {
            fetchAnew( props?.route?.params?.courseId );
        }
    }, [ props?.route?.params?.courseId ] );

    useEffect( () => {
        if ( item?.name ) {
            leaveCrumb( props.pageKey, item.name );
        }
    }, [ item?.name ] );

    const [ isShowingRatingForm, setShowRatingForm ] = useState( false );

    function onRatingSubmitCb( { comment, stars } ) {
        setItem( { ...data, review: { rating: stars, review: comment } } );
    }
    /**
     * Triggered when an open learning object is closed and the page should update.
     */
    const handleLearningObjectUpdate = useCallback( async () => {
        await refetch();
        setDisableLearningObjects( false );
    }, [ refetch ] );

    const bannerImageUrl = useMemo( () => {
        if ( item ) {
            const url = CourseUtils.getHeroImageUrl( item );
            return url ? url : getDefaultCourseBanner( ACTIVE_THEME_NAME );
        } else {
            return undefined;
        }
    }, [ item ] );

    const sidebarItems = useMemo( () => [
        item ? <div className='learning-type' key='type'>
            <Translate.Text 
                value="learning.title" />

            { item.isIlt ? 
                <Translate.Text 
                    value="ilt.tag" /> :
                <Translate.Text 
                    value="elearning.tag" /> 
            }
        </div> : null,
        <div className="rating-block" key='rating-average'>
            <Rate
                className="course-rating"
                allowHalf
                disabled
                value={ item?.ratingAverage || item?.review?.rating }
            />
            <p className="rating-label">
                <Translate.Text value="page.courseDetails.sections.ratings.averageRatingLabel" />
            </p>
        </div>,
        ( item?.status == 'COMPLETED' ) ?
            ( <div className="button-center" key='rating-user'>
                <Button
                    className="learning-action rating-toggle "
                    type="text"
                    disabled={ isShowingRatingForm }
                    onClick={ () => setShowRatingForm( true ) }
                >
                    <Translate.Text value="page.courseDetails.sections.ratings.actions.rateCourse.label" />
                </Button>
            </div> ) : null,
        ( item?.certificate?.hasCertificate && !item?.prerequisiteData?.locked ) ?
            <div key='certificate' >
                <Certificate 
                    isEarned={ item.certificate.earned } 
                    fetchCertificateUrl={ item.fetchCertificateUrl } >
                        { 
                            item.certificate.earned 
                                ? <Translate.Text value='certificate.earned' />
                                : <Translate.Text value='certificate.unearned' /> 
                        }
                </Certificate>
            </div> : null,

        ( item?.parents?.length > 0 ) ?
            <div className='curriculum-parents' key='curriculum-parents'>
                <Translate.Text 
                    value="page.curriculumDetails.sections.description.header" />
                    { item.parents.map( p => <Link key={ p.curriculumId } push to={ Routes.CurriculumDetails.buildPath( p.curriculumId ) }>{ p.name }</Link> ) }
            </div> : null
    ].filter( x => !!x ).flatMap( ( x, i ) => i == 0 ? [ x ] : [ <div className='divider' key={ `div-${ i }` } />, x ] )
    , [ item, isShowingRatingForm, item?.review ] );

    return (
        <PageContent className="page-course-details">
            <LoadingPopup visible={ loading } />
        
            { /* Banner */ }
            <PageContainer className="course-details-banner-container">
                <InfoBanner
                    title={ item?.name }
                    hidePercentage={ true }
                    percentComplete={ item?.progress }
                    isLoading= { loading } 
                    type={ 'course' }
                    locked={ item?.prerequisiteData?.locked }
                    imageSrc={ bannerImageUrl }
                /> 
            </PageContainer>

            { item?.prerequisiteData?.locked && 
                <div className='locked-content'>
                    <span>
                        <PaperIcon collection='ultimate' icon='Lock4' />
                        { 
                            item.prerequisiteData.operator == 'AND' ? <Translate.Text value='learningObject.locked.course.all'/> 
                                : <Translate.Text value='learningObject.locked.course.any'/> 
                        }                        
                        { item.prerequisiteData.prerequisites
                            .filter( r => !r.completed )
                            .flatMap( ( req, i ) => ( [ ( i != 0 ? ',\u00A0' : '\u00A0' ), <Link key={ req.id } push to={ req.link }>{ req.name }</Link> ] ) ) }

                    </span>
                </div>
            }

            <div className="course-details-container">

                { breadcrumbs }

                <div className='details'>
                    <h4>
                        <Translate.Text
                            className="anim-fade-in-fast"
                            value="page.courseDetails.sections.description.header" 
                        />
                    </h4>
                    <div className="anim-fade-in-fast">
                        <span dangerouslySetInnerHTML={ { __html: item?.description } } />
                    </div>
                </div>

           
                <div className='course-list'>
                    <h4>
                        <Translate.Text
                            className="anim-fade-in-fast"
                            value="page.courseDetails.sections.learningObjects.header" 
                        />
                    </h4> 
                    { 
                        !loading && item?.learningObject && !item?.isIlt ?
                            item.learningObjects.map( ( i, index ) => {
                                 
                                return ( 
                                    <div 
                                        key={ i.id }
                                        className="course-learning-list"
                                    >
                                        <LearningObjectItem
                                            key={ i.id }
                                            data={ i }
                                            learningObjectId={ i.id }
                                            courseId={ item.id }
                                            courseData={ item }
                                            status={ i.status }
                                            lessonData={ i }
                                            locked={ item.prerequisiteData?.locked }
                                            label={ item.learningObjects.length === 1 ? item.name : i.objectName }
                                            isComplete={ i.status == 'COMPLETED' }
                                            inProgress={ i.status === 'IN_PROGRESS' }
                                            onUpdate={ handleLearningObjectUpdate }
                                            disabled={ disableLearningObjects }
                                            onOpenCallback={ () => setDisableLearningObjects( true ) }
                                        />
                                    </div>
                                );
                            } )
                    :
                        !loading && !item?.isIlt ?
                            <Translate.Text
                                value="page.courseDetails.sections.learningObjects.body.noData"
                                className="anim-fade-in-fast"
                            /> : <></>
                    }
                    {   item?.isIlt ?
                            <Sessions item={ data } isLocked={ item.prerequisiteData?.locked } sessions={ ilt?.sessions } iltRegistrationInfo={ iltRegistrationInfo } updateRegistration={ updateRegistration } forceLoading={ forceLoading } loading={ iltLoading } /> : <></>
                    }
                </div>
                
                { !loading && 
                    <ResourceList 
                        resourceIds={ item?.resources?.map( r => r.id ) ?? [] }
                    />
                }

                <div className='sidebar'>
                    { sidebarItems }
                </div>


                <SimpleCollapse expand={ isShowingRatingForm } className='rating-form'>
                    <RatingSection isRated={ !!item?.review } itemId={ item?.id } review={ item?.review?.review } rating={ item?.review?.rating } onSubmitCb={ onRatingSubmitCb } onCloseFormCb={ () => setShowRatingForm( false ) } />
                </SimpleCollapse>
            </div> 
            <Modal
                className={ `${ ACTIVE_THEME_NAME } error-modal` }
                title={ error?.titleKey ? <h3>{ getString( error.titleKey ) }</h3> : '' }
                visible={ !!error }
                onOk={ () => {} }
                onCancel={ () => {} }
                closable={ false }
                footer={ [
                    <Button key='home' type='primary' onClick={ () => Navigation.push( Routes.Home.buildPath() ) } className='primary' >
                        { getString( 'navigation.home' ) }
                    </Button>,
                    <Button key='back' type='secondary' onClick={ () => Navigation.back() } className='secondary' >
                        { getString( 'navigation.back' ) }
                    </Button>,
                ] }>

                { error?.descriptionKey ? getString( error.descriptionKey ) : '' }
                
            </Modal>
        </PageContent>
    );
};
